import { useCallback, useMemo } from 'react';

import {
  IListUserCauseSelectionsResponse,
  useAddCauseSelection,
  useListUserCauseSelections,
} from '@givebacks/api/core';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, notifications } from 'ui';

import { CauseCardBase } from './CauseCardBase';

const CauseFollowCard = ({
  causeId,
  fullWidth = false,
  onOpenUnfollowModal,
}: {
  causeId?: string;
  fullWidth?: boolean;
  onOpenUnfollowModal?: () => void;
}) => {
  const { data: userCauses, refetch: refetchUserCauseSelections } = useListUserCauseSelections();

  const { mutate: addCauseSelection, isPending: isAddingCauseSelection } = useAddCauseSelection();

  const getIsFavorited = useCallback(
    (userCauses: IListUserCauseSelectionsResponse['cause_selections']) =>
      !!causeId && (userCauses ?? []).some((selection) => selection.cause_id === causeId),
    [causeId]
  );

  const isFavorited = getIsFavorited(userCauses ?? []);

  const handleFollow = useCallback(() => {
    if (!causeId) return;

    addCauseSelection(
      { causeId },
      {
        onSuccess: () => {
          notifications.show({
            variant: 'success',
            message: `You are now following this cause`,
          });
          // Refetch to ensure UI updates immediately
          void refetchUserCauseSelections();
        },
        onError: (error) => {
          notifications.show({
            variant: 'error',
            message: getErrorMessage_v2(error),
          });
        },
      }
    );
  }, [addCauseSelection, causeId, refetchUserCauseSelections]);

  const handleFollowClick = useCallback(() => {
    if (isFavorited) {
      onOpenUnfollowModal?.();
    } else {
      handleFollow();
    }
  }, [isFavorited, onOpenUnfollowModal, handleFollow]);

  const buttonText = useMemo(() => {
    return isFavorited ? 'Unfollow Organization' : 'Follow Organization';
  }, [isFavorited]);

  const isLoading = isAddingCauseSelection;

  return (
    <CauseCardBase
      causeId={causeId}
      fullWidth={fullWidth}
      ctaButton={
        <ButtonMantine
          size={fullWidth ? 'md' : 'sm'}
          fullWidth={fullWidth}
          variant="outline"
          onClick={handleFollowClick}
          loading={isLoading}
        >
          {buttonText}
        </ButtonMantine>
      }
    />
  );
};

export { CauseFollowCard };
